import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'react-bootstrap';
import axiosInstance from '../../../../services/AxiosInstance';
import Api from '../../../../constants/Api';
import { convertDoubleToStringPTBR } from '../../../../utils/ConverterUtils';
import { formatDate } from '../../../../utils/DateUtils';
import { buildWhatsappUrl } from '../../../../utils/FormatUtils';
import { successAlert } from '../../alert/defaultAlert';
// ApiError é o tratamento canônico de erro de API do projeto: separa timeout, falha de
// rede e mensagem devolvida pelo backend.
import { ApiError } from '../../Common/ApiError';
import './PaymentLinkActions.css';
import './ChargeActionButton.css';

/**
 * Monta a mensagem que acompanha o link no WhatsApp, conforme a situação da cobrança.
 *
 * "Regularizar" só vale para quem está em atraso. Quem acabou de assinar nunca deveu
 * nada, e receber esse texto dá a entender que já está devendo — por isso a mensagem
 * de quem ainda não pagou fala em ativar a assinatura. Sem valor na cobrança, sobra a
 * versão neutra, que serve aos dois casos.
 */
const buildWhatsAppMessage = (charge, paymentLink, t) => {
    const amount = charge?.amount != null
        ? `${t('currency_symbol')} ${convertDoubleToStringPTBR(charge.amount)}`
        : null;
    const dueDate = charge?.dueDate ? formatDate(charge.dueDate) : null;
    const chargeStatus = charge?.chargeStatus || charge?.status;
    const isOverdue = charge?.isOverdue === true || chargeStatus === 'OVERDUE';

    if (isOverdue && amount && dueDate) {
        return t('whatsapp_payment_message_overdue', { amount, dueDate, link: paymentLink });
    }

    if (amount) {
        return t('whatsapp_payment_message_pending', { amount, link: paymentLink });
    }

    return t('whatsapp_payment_message_default', { link: paymentLink });
};

/**
 * Ações do link de pagamento de uma cobrança: abrir, copiar, enviar por WhatsApp e,
 * quando o link ainda não existe, pedir a geração ao provedor.
 *
 * Usado na tela de cobranças e no detalhe da assinatura — é lá que o operador vai
 * parar depois de cadastrar um assinante pelo fluxo "enviar link para o cliente".
 *
 * Cobrança de cartão em aberto ganha ainda a ação de liberar o PIX: a assinatura Asaas
 * nasce fixada em cartão, e sem isso o assinante que não tem um fica sem como pagar.
 *
 * @param {object} charge cobrança com paymentLink e/ou providerChargeId
 * @param {string} phone telefone do cliente; sem ele o botão do WhatsApp não aparece
 * @param {function} onLinkGenerated recebe o link recém-gerado para a tela atualizar seu estado
 * @param {function} onPixEnabled recebe o link após a liberação do PIX
 * @param {boolean} compact reduz os botões para caber dentro de uma linha de tabela
 */
const PaymentLinkActions = ({ charge, phone, onLinkGenerated, onPixEnabled, compact = false }) => {

    const { t } = useTranslation();
    const [generating, setGenerating] = useState(false);
    const [enablingPix, setEnablingPix] = useState(false);

    const paymentLink = charge?.paymentLink;
    const chargeStatus = charge?.chargeStatus || charge?.status;

    // Quem decide se o link morreu é o servidor: é o mesmo cálculo que muda o status da
    // cobrança, e a tela não pode chegar a uma conclusão diferente. O status EXPIRED entra
    // junto porque os endpoints antigos, que não devolvem a marca, ainda mandam o status.
    const linkExpired = charge?.paymentLinkExpired === true || chargeStatus === 'EXPIRED';
    const size = compact ? 'sm' : undefined;
    const wrapperClass = `payment-link-actions${compact ? ' payment-link-actions--compact' : ''}`;

    // O PIX só pode ser liberado no Asaas enquanto a cobrança está em PENDING
    // ("Aguardando pagamento"). A API de listagem usa chargeStatus, enquanto o endpoint
    // resumido usa status; aceitar os dois mantém este componente reutilizável sem abrir
    // a ação para cobranças vencidas, pagas ou já configuradas como PIX.
    const canEnablePix = charge?.paymentProvider === 'ASAAS'
        && !linkExpired
        && chargeStatus === 'PENDING'
        && String(charge?.paymentMethod || '').toUpperCase() !== 'PIX';

    // Troca a forma da cobrança no provedor. A cobrança continua a mesma: o cliente não
    // recebe uma segunda fatura para o mesmo mês.
    const enablePix = () => {
        setEnablingPix(true);
        axiosInstance.post(`${Api.subscription.charge}/${charge.id}/enable-pix`)
            .then(({ data }) => {
                onPixEnabled?.(data);
                successAlert(t('pix_enabled_success'));
            })
            .catch((error) => ApiError(error))
            .finally(() => setEnablingPix(false));
    };

    // variant="" tira as cores do tema: o fundo suave de cada ação vem de ChargeActionButton.css.
    const pixButton = canEnablePix && (
        <Button variant="" className="charge-action-btn charge-action-btn--pix" size={size}
            disabled={enablingPix} onClick={enablePix}>
            {enablingPix ? (
                <>
                    <i className="fa-solid fa-spinner-third fa-spin me-1"></i>
                    {t('pix_enabling')}
                </>
            ) : (
                <>
                    <i className="fa-brands fa-pix me-1"></i>
                    {t('enable_pix')}
                </>
            )}
        </Button>
    );

    // Pede o link ao provedor. O backend devolve o existente quando já houver um,
    // então repetir a ação não gera cobrança nem link duplicado.
    const generateLink = () => {
        setGenerating(true);
        axiosInstance.post(`${Api.subscription.charge}/${charge.id}/payment-link`)
            .then(({ data }) => {
                onLinkGenerated?.(data);
                successAlert(t('payment_link_generated_success'));
            })
            .catch((error) => ApiError(error))
            .finally(() => setGenerating(false));
    };

    const copyLink = () => {
        navigator.clipboard.writeText(paymentLink)
            .then(() => successAlert(t('payment_link_copied_success')));
    };

    // Link expirado cai no mesmo caminho de "não tem link": abrir, copiar ou mandar no
    // WhatsApp um endereço que não aceita mais pagamento é pior do que não ter botão, porque
    // o cliente recebe o link, tenta pagar e volta reclamando.
    if (!paymentLink || linkExpired) {
        // Sem vínculo com o provedor a cobrança ainda não existe lá, mas isso não pode
        // significar tela sem saída: é justamente o placeholder do link recorrente, criado
        // aqui antes de o contrato nascer no provedor. Em "Aguardando pagamento" o backend
        // sabe abrir o link (generateOrGetPaymentLink devolve o existente quando já houver,
        // então repetir não duplica nada); nos demais status não há o que oferecer.
        //
        // O link expirado segue como exceção própria: ali o vínculo é o checkout guardado na
        // assinatura, e não uma cobrança do provedor.
        if (!charge?.providerChargeId && !linkExpired && chargeStatus !== 'PENDING') {
            return null;
        }

        return (
            <div className={wrapperClass}>
                {linkExpired && (
                    <span className="payment-link-actions__expired">
                        <i className="fa-solid fa-link-slash me-1"></i>
                        {t('payment_link_expired_hint')}
                    </span>
                )}
                {pixButton}
                <Button variant="" className="charge-action-btn charge-action-btn--link" size={size}
                    disabled={generating} onClick={generateLink}>
                    {generating ? (
                        <>
                            <i className="fa-solid fa-spinner-third fa-spin me-1"></i>
                            {t('payment_link_generating')}
                        </>
                    ) : (
                        <>
                            <i className="fas fa-link me-1"></i>
                            {linkExpired ? t('regenerate_payment_link') : t('generate_payment_link')}
                        </>
                    )}
                </Button>
            </div>
        );
    }

    return (
        <div className={wrapperClass}>
            {pixButton}
            <a
                href={paymentLink}
                target="_blank"
                rel="noopener noreferrer"
                className={`btn charge-action-btn charge-action-btn--link${compact ? ' btn-sm' : ''}`}
            >
                <i className="fas fa-external-link-alt me-1"></i>
                {t('payment_link')}
            </a>
            <Button variant="" className="charge-action-btn charge-action-btn--neutral" size={size} onClick={copyLink}>
                <i className="fas fa-copy me-1"></i>
                {t('payment_link_copy')}
            </Button>
            {phone && (
                <Button
                    variant="success"
                    size={size}
                    className="payment-link-actions__whatsapp"
                    onClick={() => window.open(
                        buildWhatsappUrl(phone, buildWhatsAppMessage(charge, paymentLink, t)),
                        '_blank'
                    )}
                >
                    <i className="fab fa-whatsapp me-1"></i>
                    WhatsApp
                </Button>
            )}
        </div>
    );
};

export default PaymentLinkActions;
